/**
 * Themed View — shorthand props for spacing, sizing, flex, and color.
 *
 * Resolves named colors and spacing tokens from the Unistyles theme.
 * Font-agnostic, same as the companion Text component.
 */
import { forwardRef } from "react";
import { View as RNView, type ViewProps as RNViewProps, type ViewStyle, type ColorValue } from "react-native";
import "../theme"; // module augmentation for react-native-unistyles types
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import type { AppColorPalette, AppColorPaletteShades, AppSpacing } from "../theme";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

type ViewColorPalette = AppColorPalette | AppColorPaletteShades;

export type ViewProps = RNViewProps & {
  readonly bg?: ViewColorPalette;
  readonly backgroundColor?: ViewColorPalette;
  readonly p?: AppSpacing;
  readonly padding?: AppSpacing;
  readonly px?: AppSpacing;
  readonly paddingX?: AppSpacing;
  readonly py?: AppSpacing;
  readonly paddingY?: AppSpacing;
  readonly pt?: AppSpacing;
  readonly paddingTop?: AppSpacing;
  readonly pb?: AppSpacing;
  readonly paddingBottom?: AppSpacing;
  readonly pl?: AppSpacing;
  readonly paddingLeft?: AppSpacing;
  readonly pr?: AppSpacing;
  readonly paddingRight?: AppSpacing;
  readonly m?: AppSpacing;
  readonly margin?: AppSpacing;
  readonly mx?: AppSpacing;
  readonly marginX?: AppSpacing;
  readonly my?: AppSpacing;
  readonly marginY?: AppSpacing;
  readonly mt?: AppSpacing;
  readonly marginTop?: AppSpacing;
  readonly mb?: AppSpacing;
  readonly marginBottom?: AppSpacing;
  readonly ml?: AppSpacing;
  readonly marginLeft?: AppSpacing;
  readonly mr?: AppSpacing;
  readonly marginRight?: AppSpacing;
  readonly w?: ViewStyle["width"];
  readonly width?: ViewStyle["width"];
  readonly h?: ViewStyle["height"];
  readonly height?: ViewStyle["height"];
  readonly minW?: ViewStyle["minWidth"];
  readonly minWidth?: ViewStyle["minWidth"];
  readonly maxW?: ViewStyle["maxWidth"];
  readonly maxWidth?: ViewStyle["maxWidth"];
  readonly minH?: ViewStyle["minHeight"];
  readonly minHeight?: ViewStyle["minHeight"];
  readonly maxH?: ViewStyle["maxHeight"];
  readonly maxHeight?: ViewStyle["maxHeight"];
  readonly borderRadius?: ViewStyle["borderRadius"];
  readonly rounded?: ViewStyle["borderRadius"];
  readonly borderCurve?: ViewStyle["borderCurve"];
  readonly borderColor?: ViewColorPalette;
  readonly borderWidth?: ViewStyle["borderWidth"];
  readonly borderTopWidth?: ViewStyle["borderTopWidth"];
  readonly borderBottomWidth?: ViewStyle["borderBottomWidth"];
  readonly borderLeftWidth?: ViewStyle["borderLeftWidth"];
  readonly borderRightWidth?: ViewStyle["borderRightWidth"];
  readonly shadow?: "sm" | "md" | "lg" | "xl" | "2xl";
  readonly gap?: AppSpacing;
  readonly rowGap?: AppSpacing;
  readonly columnGap?: AppSpacing;
  readonly flex?: ViewStyle["flex"];
  readonly flexDirection?: ViewStyle["flexDirection"];
  readonly flexWrap?: ViewStyle["flexWrap"];
  readonly flexGrow?: ViewStyle["flexGrow"];
  readonly flexShrink?: ViewStyle["flexShrink"];
  readonly flexBasis?: ViewStyle["flexBasis"];
  readonly alignItems?: ViewStyle["alignItems"];
  readonly alignSelf?: ViewStyle["alignSelf"];
  readonly alignContent?: ViewStyle["alignContent"];
  readonly justifyContent?: ViewStyle["justifyContent"];
};

// ---------------------------------------------------------------------------
// Color resolution — type predicate instead of `as` cast
// ---------------------------------------------------------------------------

function isStringRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null;
}

function useViewColor(): (colorProp: ViewColorPalette | undefined) => ColorValue | undefined {
  const { theme } = useUnistyles();
  const isDarkMode = theme.currentTheme === "dark";

  return (colorProp: ViewColorPalette | undefined): ColorValue | undefined => {
    if (colorProp === undefined) return undefined;

    const color: unknown = theme.colors[colorProp];

    if (typeof color === "string") return color;

    if (isStringRecord(color)) {
      const shade = isDarkMode ? "800" : "200";
      const resolved: unknown = color[shade];
      if (typeof resolved === "string") return resolved;
    }

    return undefined;
  };
}

// ---------------------------------------------------------------------------
// Style props — exact union types instead of optional undefined
// ---------------------------------------------------------------------------

interface ViewStyleProps {
  readonly bg: ColorValue | undefined;
  readonly p: AppSpacing | undefined;
  readonly px: AppSpacing | undefined;
  readonly py: AppSpacing | undefined;
  readonly pt: AppSpacing | undefined;
  readonly pb: AppSpacing | undefined;
  readonly pl: AppSpacing | undefined;
  readonly pr: AppSpacing | undefined;
  readonly m: AppSpacing | undefined;
  readonly mx: AppSpacing | undefined;
  readonly my: AppSpacing | undefined;
  readonly mt: AppSpacing | undefined;
  readonly mb: AppSpacing | undefined;
  readonly ml: AppSpacing | undefined;
  readonly mr: AppSpacing | undefined;
  readonly w: ViewStyle["width"] | undefined;
  readonly h: ViewStyle["height"] | undefined;
  readonly minW: ViewStyle["minWidth"] | undefined;
  readonly maxW: ViewStyle["maxWidth"] | undefined;
  readonly minH: ViewStyle["minHeight"] | undefined;
  readonly maxH: ViewStyle["maxHeight"] | undefined;
  readonly borderRadius: ViewStyle["borderRadius"] | undefined;
  readonly borderCurve: ViewStyle["borderCurve"] | undefined;
  readonly borderColor: ColorValue | undefined;
  readonly borderWidth: ViewStyle["borderWidth"] | undefined;
  readonly borderTopWidth: ViewStyle["borderTopWidth"] | undefined;
  readonly borderBottomWidth: ViewStyle["borderBottomWidth"] | undefined;
  readonly borderLeftWidth: ViewStyle["borderLeftWidth"] | undefined;
  readonly borderRightWidth: ViewStyle["borderRightWidth"] | undefined;
  readonly gap: AppSpacing | undefined;
  readonly rowGap: AppSpacing | undefined;
  readonly columnGap: AppSpacing | undefined;
  readonly flex: ViewStyle["flex"] | undefined;
  readonly flexDirection: ViewStyle["flexDirection"] | undefined;
  readonly flexWrap: ViewStyle["flexWrap"] | undefined;
  readonly flexGrow: ViewStyle["flexGrow"] | undefined;
  readonly flexShrink: ViewStyle["flexShrink"] | undefined;
  readonly flexBasis: ViewStyle["flexBasis"] | undefined;
  readonly alignItems: ViewStyle["alignItems"] | undefined;
  readonly alignSelf: ViewStyle["alignSelf"] | undefined;
  readonly alignContent: ViewStyle["alignContent"] | undefined;
  readonly justifyContent: ViewStyle["justifyContent"] | undefined;
}

const resolveStyles = (props: ViewStyleProps) =>
  StyleSheet.create((theme) => ({
    container: {
      backgroundColor: props.bg,
      padding: props.p ? theme.spacing[props.p] : undefined,
      paddingHorizontal: props.px ? theme.spacing[props.px] : undefined,
      paddingVertical: props.py ? theme.spacing[props.py] : undefined,
      paddingTop: props.pt ? theme.spacing[props.pt] : undefined,
      paddingBottom: props.pb ? theme.spacing[props.pb] : undefined,
      paddingLeft: props.pl ? theme.spacing[props.pl] : undefined,
      paddingRight: props.pr ? theme.spacing[props.pr] : undefined,
      margin: props.m ? theme.spacing[props.m] : undefined,
      marginHorizontal: props.mx ? theme.spacing[props.mx] : undefined,
      marginVertical: props.my ? theme.spacing[props.my] : undefined,
      marginTop: props.mt ? theme.spacing[props.mt] : undefined,
      marginBottom: props.mb ? theme.spacing[props.mb] : undefined,
      marginLeft: props.ml ? theme.spacing[props.ml] : undefined,
      marginRight: props.mr ? theme.spacing[props.mr] : undefined,
      width: props.w,
      height: props.h,
      minWidth: props.minW,
      maxWidth: props.maxW,
      minHeight: props.minH,
      maxHeight: props.maxH,
      borderRadius: props.borderRadius,
      borderCurve: props.borderCurve,
      borderColor: props.borderColor,
      borderWidth: props.borderWidth,
      borderTopWidth: props.borderTopWidth,
      borderBottomWidth: props.borderBottomWidth,
      borderLeftWidth: props.borderLeftWidth,
      borderRightWidth: props.borderRightWidth,
      gap: props.gap ? theme.spacing[props.gap] : undefined,
      rowGap: props.rowGap ? theme.spacing[props.rowGap] : undefined,
      columnGap: props.columnGap ? theme.spacing[props.columnGap] : undefined,
      flex: props.flex,
      flexDirection: props.flexDirection,
      flexWrap: props.flexWrap,
      flexGrow: props.flexGrow,
      flexShrink: props.flexShrink,
      flexBasis: props.flexBasis,
      alignItems: props.alignItems,
      alignSelf: props.alignSelf,
      alignContent: props.alignContent,
      justifyContent: props.justifyContent,
    },
  }));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const View = forwardRef<RNView, ViewProps>(
  (
    {
      style,
      bg,
      backgroundColor,
      p,
      padding,
      px,
      paddingX,
      py,
      paddingY,
      pt,
      paddingTop,
      pb,
      paddingBottom,
      pl,
      paddingLeft,
      pr,
      paddingRight,
      m,
      margin,
      mx,
      marginX,
      my,
      marginY,
      mt,
      marginTop,
      mb,
      marginBottom,
      ml,
      marginLeft,
      mr,
      marginRight,
      w,
      width,
      h,
      height,
      minW,
      minWidth,
      maxW,
      maxWidth,
      minH,
      minHeight,
      maxH,
      maxHeight,
      borderRadius,
      rounded,
      borderCurve,
      borderColor,
      borderWidth,
      borderTopWidth,
      borderBottomWidth,
      borderLeftWidth,
      borderRightWidth,
      shadow,
      gap,
      rowGap,
      columnGap,
      flex,
      flexDirection,
      flexWrap,
      flexGrow,
      flexShrink,
      flexBasis,
      alignItems,
      alignSelf,
      alignContent,
      justifyContent,
      ...otherProps
    },
    ref,
  ) => {
    const { theme } = useUnistyles();
    const resolveColor = useViewColor();

    return (
      <RNView
        ref={ref}
        style={[
          resolveStyles({
            bg: resolveColor(bg ?? backgroundColor),
            p: p ?? padding,
            px: px ?? paddingX,
            py: py ?? paddingY,
            pt: pt ?? paddingTop,
            pb: pb ?? paddingBottom,
            pl: pl ?? paddingLeft,
            pr: pr ?? paddingRight,
            m: m ?? margin,
            mx: mx ?? marginX,
            my: my ?? marginY,
            mt: mt ?? marginTop,
            mb: mb ?? marginBottom,
            ml: ml ?? marginLeft,
            mr: mr ?? marginRight,
            w: w ?? width,
            h: h ?? height,
            minW: minW ?? minWidth,
            maxW: maxW ?? maxWidth,
            minH: minH ?? minHeight,
            maxH: maxH ?? maxHeight,
            borderRadius: borderRadius ?? rounded,
            borderCurve,
            borderColor: resolveColor(borderColor),
            borderWidth,
            borderTopWidth,
            borderBottomWidth,
            borderLeftWidth,
            borderRightWidth,
            gap,
            rowGap,
            columnGap,
            flex,
            flexDirection,
            flexWrap,
            flexGrow,
            flexShrink,
            flexBasis,
            alignItems,
            alignSelf,
            alignContent,
            justifyContent,
          }).container,
          shadow !== undefined ? theme.shadows[shadow] : undefined,
          style,
        ]}
        {...otherProps}
      />
    );
  },
);
